/** Deterministic random numbers. Every random draw in rules goes through an `Rng` so battles and pulls replay bit-identically. */
export interface Rng {
  /** Uniform float in [0, 1). */
  next(): number;
  /** Uniform integer in [0, maxExclusive). */
  int(maxExclusive: number): number;
  pick<T>(arr: readonly T[]): T;
  chance(p: number): boolean;
  /** Current internal state (for persisting mid-stream). */
  state(): number;
}

/** mulberry32: small, fast, good enough for gameplay; 32-bit state. */
export function mulberry32(seed: number): Rng {
  let s = seed >>> 0;
  const next = (): number => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return {
    next,
    int: (max) => {
      if (max <= 0) throw new RangeError("int(max) needs max > 0");
      return Math.floor(next() * max);
    },
    pick: (arr) => {
      if (arr.length === 0) throw new RangeError("pick from empty array");
      return arr[Math.floor(next() * arr.length)] as (typeof arr)[number];
    },
    chance: (p) => next() < p,
    state: () => s,
  };
}

/** FNV-1a 32-bit hash over the string form of the parts; stable across runs and platforms. */
export function hashSeed(...parts: (string | number)[]): number {
  let h = 0x811c9dc5;
  const str = parts.map(String).join("");
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Weighted choice; weights need not sum to 1. */
export function weightedPick<T>(rng: Rng, entries: readonly { value: T; weight: number }[]): T {
  const total = entries.reduce((s, e) => s + e.weight, 0);
  if (total <= 0) throw new RangeError("weightedPick needs positive total weight");
  let r = rng.next() * total;
  for (const e of entries) {
    r -= e.weight;
    if (r < 0) return e.value;
  }
  return entries[entries.length - 1]!.value;
}
